/**
 * BioUpdatesPanel — pending Bio Update System claim review queue
 * for one matchId pairing (user_persona + match_persona).
 *
 * Spec: Bio Update System Dev Brief v1.1 §8 (FE-1 ... FE-15).
 *
 * The panel personalises every header and label with the actual persona
 * names (per Adeon's instruction: never "USER" or "MATCH" in copy).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  BookOpen,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ListPlus,
  Lock,
  Loader2,
  PencilLine,
  RefreshCw,
  ShieldAlert,
  Sparkle,
  X,
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { authAxios } from "../../utils/auth";
import { appendMerge, firstName } from "./bioUpdatesHelpers";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

// Sections 1-15 grouped into four UI bands per FE-3.
const SECTION_GROUPS = [
  { key: "identity",     label: "Identity",     sections: [1, 2, 3, 4] },
  { key: "lifestyle",    label: "Lifestyle",    sections: [7, 8, 9, 10, 11] },
  { key: "relationship", label: "Relationship", sections: [5, 6, 13, 14] },
  { key: "sensitive",    label: "Sensitive",    sections: [12, 15] },
];

const CONFIDENCE_ORDER = { high: 3, medium: 2, low: 1 };

// Hardcoded batch minimum per Q16=a.
const BATCH_MIN_CLAIMS = 3;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function humanizeField(name) {
  if (!name) return "";
  return name
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatValue(v) {
  if (v == null) return "—";
  if (Array.isArray(v)) return v.join(", ");
  if (typeof v === "boolean") return v ? "Yes" : "No";
  return String(v);
}

function groupBySection(items) {
  const out = {};
  for (const g of SECTION_GROUPS) out[g.key] = [];
  for (const item of items) {
    const group = SECTION_GROUPS.find((g) => g.sections.includes(item.field_section));
    if (group) out[group.key].push(item);
  }
  return out;
}

function sortClaims(rows) {
  return [...rows].sort((a, b) => {
    const c =
      (CONFIDENCE_ORDER[b.confidence] || 0) - (CONFIDENCE_ORDER[a.confidence] || 0);
    if (c !== 0) return c;
    if (a.field_tier !== b.field_tier) return a.field_tier - b.field_tier;
    return new Date(b.created_at || 0) - new Date(a.created_at || 0);
  });
}

// ---------------------------------------------------------------------------
// Confidence dot + tier chip
// ---------------------------------------------------------------------------

function ConfidenceDot({ confidence }) {
  const colour =
    confidence === "high"
      ? "bg-emerald-400"
      : confidence === "medium"
      ? "bg-amber-400"
      : "bg-white/40";
  return (
    <span
      className={`inline-block w-2 h-2 rounded-full ${colour}`}
      title={`Confidence: ${confidence}`}
      data-testid={`bio-claim-confidence-${confidence}`}
    />
  );
}

function TierChip({ tier }) {
  const t = String(tier);
  const cls = {
    "1": "bg-sky-500/15 text-sky-200 border-sky-400/30",
    "2": "bg-teal-500/15 text-teal-200 border-teal-400/30",
    "3": "bg-violet-500/15 text-violet-200 border-violet-400/30",
    "4": "bg-rose-500/15 text-rose-200 border-rose-400/30",
  }[t] || "bg-white/10 text-white/70 border-white/20";
  return (
    <span
      className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border ${cls}`}
      data-testid={`bio-claim-tier-${t}`}
    >
      T{t}
    </span>
  );
}

// ---------------------------------------------------------------------------
// ClaimCard
// ---------------------------------------------------------------------------

function ClaimCard({ claim, onAction, busy, personaFirst }) {
  const [evidenceOpen, setEvidenceOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(() =>
    Array.isArray(claim.proposed_value)
      ? claim.proposed_value.join(", ")
      : claim.proposed_value ?? ""
  );
  const [confirmingTier4, setConfirmingTier4] = useState(false);

  const isTier4 = claim.field_tier === 4;
  // Append is offered whenever there's an existing value we can add to
  // and the field type supports concatenation (textarea) / union
  // (multi_select). We intentionally do NOT gate on action === "supersede"
  // because the LLM sometimes labels the same shape as "create" on the
  // match persona even when a current_value is present.
  const canAppend =
    (claim.field_type === "textarea" || claim.field_type === "multi_select") &&
    claim.current_value != null &&
    claim.current_value !== "" &&
    !(Array.isArray(claim.current_value) && claim.current_value.length === 0);
  const borderClass = isTier4
    ? "border-l-4 border-l-rose-500/70 border-divine-gold/10"
    : "border border-divine-gold/15";

  const parseEditedValue = () => {
    if (Array.isArray(claim.proposed_value)) {
      return editValue.split(",").map((s) => s.trim()).filter(Boolean);
    }
    if (typeof claim.proposed_value === "number") {
      const n = Number(editValue);
      return Number.isNaN(n) ? claim.proposed_value : n;
    }
    return editValue;
  };

  const handleApprove = (lock = false) => {
    if (isTier4 && !confirmingTier4 && !lock) {
      setConfirmingTier4(true);
      return;
    }
    onAction("approve", claim, { lock });
  };

  return (
    <div
      className={`rounded-md ${borderClass} bg-divine-black/30 p-3 space-y-2`}
      data-testid={`bio-claim-card-${claim.claim_id}`}
    >
      {isTier4 && (
        <div className="text-[11px] text-rose-300 flex items-center gap-1">
          <ShieldAlert className="w-3 h-3" /> Sensitive information
        </div>
      )}

      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-divine-gold text-sm font-medium">
              {personaFirst ? `${personaFirst}: ` : ""}{humanizeField(claim.field_name)}
            </span>
            <TierChip tier={claim.field_tier} />
            <ConfidenceDot confidence={claim.confidence} />
            {claim.elicited && (
              <span
                className="text-[10px] px-1.5 py-0.5 rounded bg-divine-gold/10 text-divine-gold/80 border border-divine-gold/30"
                data-testid="bio-claim-elicited-badge"
              >
                Asked in coaching
              </span>
            )}
            {claim.overrides_user_input && (
              <span
                className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-400/30"
                data-testid="bio-claim-overrides-badge"
              >
                Overrides your previous entry
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Value display: Currently | Proposed diff whenever a live value
          is on file (regardless of whether the digest writer labeled the
          claim "supersede" or "create"). Stale "create" claims emitted
          against fields that already have data should still surface the
          comparison so the reviewer can see they match. */}
      {claim.current_value != null ? (
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div className="rounded border border-white/10 bg-white/5 p-2">
            <div className="text-[10px] uppercase text-white/40 mb-1">Currently</div>
            <div className="text-white/60 line-through">{formatValue(claim.current_value)}</div>
          </div>
          <div className="rounded border border-divine-gold/30 bg-divine-gold/5 p-2">
            <div className="text-[10px] uppercase text-divine-gold/60 mb-1">Proposed</div>
            {editing ? (
              <textarea
                autoFocus
                rows={Math.max(2, Math.min(20, String(editValue ?? "").split("\n").reduce((n, ln) => n + Math.max(1, Math.ceil(ln.length / 60)), 0)))}
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                className="w-full bg-black/40 border border-divine-gold/30 rounded p-2 text-white text-sm focus:outline-none focus:border-divine-gold/60 leading-relaxed resize-y"
                data-testid="bio-claim-edit-input"
              />
            ) : (
              <div className="text-divine-gold whitespace-pre-wrap break-words">{formatValue(claim.proposed_value)}</div>
            )}
          </div>
        </div>
      ) : (
        <div className="rounded border border-divine-gold/30 bg-divine-gold/5 p-2 text-sm">
          {editing ? (
            <textarea
              autoFocus
              rows={Math.max(2, Math.min(20, String(editValue ?? "").split("\n").reduce((n, ln) => n + Math.max(1, Math.ceil(ln.length / 60)), 0)))}
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              className="w-full bg-black/40 border border-divine-gold/30 rounded p-2 text-white text-sm focus:outline-none focus:border-divine-gold/60 leading-relaxed resize-y"
              data-testid="bio-claim-edit-input"
            />
          ) : (
            <span className="text-divine-gold whitespace-pre-wrap break-words">{formatValue(claim.proposed_value)}</span>
          )}
        </div>
      )}

      {/* Evidence summary, collapsible */}
      {claim.evidence_summary ? (
        <button
          type="button"
          onClick={() => setEvidenceOpen((v) => !v)}
          className="text-[11px] text-white/50 hover:text-white/80 flex items-center gap-1"
          data-testid="bio-claim-evidence-toggle"
        >
          {evidenceOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          Evidence
        </button>
      ) : null}
      {evidenceOpen && claim.evidence_summary && (
        <div className="text-[12px] text-white/60 italic px-2">
          {claim.evidence_summary}
          {claim.evidence_quote && (
            <div className="text-white/50 not-italic mt-1">
              "{claim.evidence_quote}"
            </div>
          )}
        </div>
      )}

      {/* Tier-4 confirm dialog inline */}
      {confirmingTier4 && (
        <div
          className="rounded border border-rose-500/40 bg-rose-500/10 p-2 text-sm text-rose-100 space-y-2"
          data-testid="bio-claim-tier4-confirm"
        >
          <div>
            This is a sensitive field. You can save it without locking, or
            save and lock it against further changes from Eros.
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setConfirmingTier4(false);
                onAction("approve", claim, { lock: false });
              }}
              className="px-3 py-1 text-xs rounded bg-emerald-500/20 hover:bg-emerald-500/40 text-emerald-100 font-medium border border-emerald-400/40 disabled:opacity-40"
              data-testid="bio-claim-tier4-confirm-just-save"
            >
              Just save
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setConfirmingTier4(false);
                onAction("approve", claim, { lock: true });
              }}
              className="px-3 py-1 text-xs rounded bg-rose-500/20 hover:bg-rose-500/40 text-rose-100 font-medium border border-rose-400/40 disabled:opacity-40"
              data-testid="bio-claim-tier4-confirm-yes"
            >
              Save & lock
            </button>
            <button
              type="button"
              onClick={() => setConfirmingTier4(false)}
              className="px-3 py-1 text-xs rounded bg-white/5 hover:bg-white/10 text-white/80 border border-white/15"
              data-testid="bio-claim-tier4-confirm-no"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Action row */}
      {!confirmingTier4 && (
        <div className="flex flex-wrap gap-2 pt-1">
          {editing ? (
            <>
              <button
                type="button"
                disabled={busy}
                onClick={() =>
                  onAction("modify", claim, { modified_value: parseEditedValue() })
                }
                className="px-3 py-1 text-xs rounded bg-divine-gold/20 hover:bg-divine-gold/30 text-divine-gold font-medium border border-divine-gold/40 flex items-center gap-1 disabled:opacity-40"
                data-testid="bio-claim-modify-save"
              >
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save change"}
              </button>
              <button
                type="button"
                onClick={() => setEditing(false)}
                className="px-3 py-1 text-xs rounded bg-white/5 hover:bg-white/10 text-white/80 border border-white/15"
                data-testid="bio-claim-modify-cancel"
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                disabled={busy}
                onClick={() => handleApprove(false)}
                className="px-3 py-1 text-xs rounded bg-divine-gold/20 hover:bg-divine-gold/30 text-divine-gold font-medium border border-divine-gold/40 flex items-center gap-1 disabled:opacity-40"
                data-testid="bio-claim-approve"
              >
                <CheckCircle2 className="w-3 h-3" /> Approve
              </button>
              {canAppend && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    const merged = appendMerge({
                      current: claim.current_value,
                      proposed: claim.proposed_value,
                      fieldType: claim.field_type,
                      maxSelections: claim.max_selections,
                    });
                    onAction("modify", claim, { modified_value: merged });
                  }}
                  className="px-3 py-1 text-xs rounded bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-200 border border-emerald-400/30 flex items-center gap-1 disabled:opacity-40"
                  data-testid="bio-claim-append"
                  title="Keep the current value and add the new information to it"
                >
                  <ListPlus className="w-3 h-3" /> Append
                </button>
              )}
              {!isTier4 && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => handleApprove(true)}
                  className="px-3 py-1 text-xs rounded bg-divine-black/40 hover:bg-divine-gold/15 text-divine-gold/80 hover:text-divine-gold border border-divine-gold/30 flex items-center gap-1 disabled:opacity-40"
                  data-testid="bio-claim-approve-and-lock"
                >
                  <Lock className="w-3 h-3" /> Approve & lock
                </button>
              )}
              <button
                type="button"
                disabled={busy}
                onClick={() => setEditing(true)}
                className="px-3 py-1 text-xs rounded bg-white/5 hover:bg-white/10 text-white/80 border border-white/15 flex items-center gap-1 disabled:opacity-40"
                data-testid="bio-claim-modify-start"
              >
                <PencilLine className="w-3 h-3" /> Modify
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => onAction("reject", claim)}
                className="px-3 py-1 text-xs rounded bg-rose-500/10 hover:bg-rose-500/20 text-rose-200 border border-rose-400/30 flex items-center gap-1 ml-auto disabled:opacity-40"
                data-testid="bio-claim-reject"
              >
                <X className="w-3 h-3" /> Reject
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// NarrativeSynthesisCard — Phase 1 history_together review card
// ---------------------------------------------------------------------------
//
// Surfaces a digest-emitted history_together replacement as a side-by-side
// diff with inline textarea editing. Distinct from ClaimCard because:
//  - The proposed value is multi-paragraph narrative, not a single string.
//  - Approve commits the full synthesised replacement to db.matches via the
//    same apply_bio_value path; no Tier-4 lock UX.
//  - Editing uses a textarea, not an input.
//  - Endpoints live under /bio-claims/narrative/* (sibling collection).

function NarrativeSynthesisCard({ row, onAction, busy, personaFirst }) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(row.synthesised_value || "");
  const [evidenceOpen, setEvidenceOpen] = useState(false);

  const hasPrior = !!(row.prior_value && row.prior_value.trim());

  const handleApprove = () => onAction("approve", row);
  const handleReject = () => onAction("reject", row);
  const handleModify = () =>
    onAction("modify", row, { modified_value: editValue });

  return (
    <div
      className="rounded-md border border-divine-gold/30 bg-divine-black/40 p-3 space-y-2"
      data-testid={`narrative-synthesis-card-${row.synthesis_id}`}
    >
      <div className="flex items-center gap-2 flex-wrap">
        <BookOpen className="w-3.5 h-3.5 text-divine-gold/80" />
        <span className="text-divine-gold text-sm font-medium">
          {personaFirst ? `${personaFirst}: ` : ""}{humanizeField(row.field_name)}
        </span>
        <span
          className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border bg-amber-500/15 text-amber-200 border-amber-400/30"
          data-testid="narrative-synthesis-badge"
        >
          Narrative synthesis
        </span>
        <ConfidenceDot confidence={row.confidence} />
      </div>

      <div className="text-[11px] text-white/55">
        Eros has merged what you discussed in your latest session with what was
        already on file. Review the proposed narrative below — approve as-is,
        edit it inline, or reject.
      </div>

      {/* Side-by-side diff */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
        <div className="rounded border border-white/10 bg-white/5 p-2">
          <div className="text-[10px] uppercase text-white/40 mb-1">
            Currently on file
          </div>
          <div className="text-white/70 whitespace-pre-wrap leading-relaxed">
            {hasPrior ? row.prior_value : (
              <span className="italic text-white/40">— empty —</span>
            )}
          </div>
        </div>
        <div className="rounded border border-divine-gold/30 bg-divine-gold/5 p-2">
          <div className="text-[10px] uppercase text-divine-gold/60 mb-1">
            Proposed
          </div>
          {editing ? (
            <textarea
              autoFocus
              rows={Math.max(6, Math.min(20, editValue.split("\n").length + 2))}
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              className="w-full bg-black/40 border border-divine-gold/30 rounded p-2 text-white text-sm focus:outline-none focus:border-divine-gold/60 leading-relaxed"
              data-testid="narrative-synthesis-edit-textarea"
            />
          ) : (
            <div className="text-divine-gold whitespace-pre-wrap leading-relaxed">
              {row.synthesised_value}
            </div>
          )}
        </div>
      </div>

      {/* Evidence summary */}
      {row.evidence_summary ? (
        <button
          type="button"
          onClick={() => setEvidenceOpen((v) => !v)}
          className="text-[11px] text-white/50 hover:text-white/80 flex items-center gap-1"
          data-testid="narrative-synthesis-evidence-toggle"
        >
          {evidenceOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          What changed this session
        </button>
      ) : null}
      {evidenceOpen && row.evidence_summary && (
        <div className="text-[12px] text-white/60 italic px-2">
          {row.evidence_summary}
        </div>
      )}

      {/* Action row */}
      <div className="flex flex-wrap gap-2 pt-1">
        {editing ? (
          <>
            <button
              type="button"
              disabled={busy || !editValue.trim()}
              onClick={handleModify}
              className="px-3 py-1 text-xs rounded bg-divine-gold/20 hover:bg-divine-gold/30 text-divine-gold font-medium border border-divine-gold/40 flex items-center gap-1 disabled:opacity-40"
              data-testid="narrative-synthesis-save-edit"
            >
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save edit"}
            </button>
            <button
              type="button"
              onClick={() => {
                setEditing(false);
                setEditValue(row.synthesised_value || "");
              }}
              className="px-3 py-1 text-xs rounded bg-white/5 hover:bg-white/10 text-white/80 border border-white/15"
              data-testid="narrative-synthesis-cancel-edit"
            >
              Cancel
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              disabled={busy}
              onClick={handleApprove}
              className="px-3 py-1 text-xs rounded bg-divine-gold/20 hover:bg-divine-gold/30 text-divine-gold font-medium border border-divine-gold/40 flex items-center gap-1 disabled:opacity-40"
              data-testid="narrative-synthesis-approve"
            >
              <CheckCircle2 className="w-3 h-3" /> Approve
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => setEditing(true)}
              className="px-3 py-1 text-xs rounded bg-white/5 hover:bg-white/10 text-white/80 border border-white/15 flex items-center gap-1 disabled:opacity-40"
              data-testid="narrative-synthesis-edit"
            >
              <PencilLine className="w-3 h-3" /> Edit before approving
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={handleReject}
              className="px-3 py-1 text-xs rounded bg-rose-500/10 hover:bg-rose-500/20 text-rose-200 border border-rose-400/30 flex items-center gap-1 ml-auto disabled:opacity-40"
              data-testid="narrative-synthesis-reject"
            >
              <X className="w-3 h-3" /> Reject
            </button>
          </>
        )}
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Section group block
// ---------------------------------------------------------------------------

function SectionGroup({ groupLabel, claims, onAction, busyClaimId, personaFirst }) {
  const [open, setOpen] = useState(true);
  if (claims.length === 0) return null;
  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 text-[11px] uppercase tracking-wider text-white/50 hover:text-white/80"
        data-testid={`bio-section-toggle-${groupLabel.toLowerCase()}`}
      >
        {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        {groupLabel} <span className="text-white/30">({claims.length})</span>
      </button>
      {open && (
        <div className="space-y-2 pl-1">
          {claims.map((c) => (
            <ClaimCard
              key={c.claim_id}
              claim={c}
              busy={busyClaimId === c.claim_id}
              onAction={onAction}
              personaFirst={personaFirst}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Persona pane (one tab's worth)
// ---------------------------------------------------------------------------

function PersonaPane({ personaName, claims, onAction, busyClaimId, emptyHint }) {
  const grouped = useMemo(() => groupBySection(claims), [claims]);
  const personaFirst = firstName(personaName) || personaName || "";
  if (claims.length === 0) {
    return (
      <div
        className="rounded-md border border-divine-gold/10 bg-divine-black/20 p-6 text-center text-xs text-white/50"
        data-testid={`bio-claims-pane-empty-${personaName.toLowerCase().replace(/\s+/g, "-")}`}
      >
        {emptyHint}
      </div>
    );
  }
  return (
    <div
      className="space-y-3"
      data-testid={`bio-claims-pane-${personaName.toLowerCase().replace(/\s+/g, "-")}`}
    >
      {SECTION_GROUPS.map((g) => (
        <SectionGroup
          key={g.key}
          groupLabel={g.label}
          claims={grouped[g.key]}
          onAction={onAction}
          busyClaimId={busyClaimId}
          personaFirst={personaFirst}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main panel
// ---------------------------------------------------------------------------

export default function BioUpdatesPanel({
  matchId,
  matchName,
  userPersonaId,
  userPersonaName,
  refreshKey = 0,
  onCountChange,
}) {
  const [matchClaims, setMatchClaims] = useState([]);
  const [userClaims, setUserClaims] = useState([]);
  const [narrativeRows, setNarrativeRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyClaimId, setBusyClaimId] = useState(null);
  const [busySynthesisId, setBusySynthesisId] = useState(null);
  const [batchBusy, setBatchBusy] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);

  const safeMatchName = matchName?.trim() || "this person";
  const safeUserPersonaName = userPersonaName?.trim() || "you";
  const userFirst = firstName(userPersonaName) || "You";
  const matchFirst = firstName(matchName) || "Them";
  // Avoid collision when the same first name appears on both sides
  // (e.g. "Adeon" testing himself). Suffix the match-side label.
  const matchTabLabel =
    matchFirst.toLowerCase() === userFirst.toLowerCase()
      ? `${matchFirst} (match)`
      : matchFirst;

  // Which sub-tab is active. On the first successful load, we auto-pick
  // the side with the most pending updates (ties favour the user side).
  // After that, the tab is user-controlled — approving a claim never
  // re-triggers the auto-pick, otherwise the tab would flip mid-review.
  const [activeSubTab, setActiveSubTab] = useState("user");
  const didAutoPickRef = React.useRef(false);

  const fetchClaims = useCallback(async () => {
    if (!matchId) return;
    setLoading(true);
    setError(null);
    try {
      const requests = [
        authAxios.get("/bio-claims", {
          params: {
            status: "pending",
            target_entity: "match_persona",
            target_entity_id: matchId,
            page_size: 200,
          },
        }),
        // Narrative synthesis rows for the match_persona side (Phase 1).
        authAxios.get("/bio-claims/narrative", {
          params: {
            status: "pending",
            target_entity: "match_persona",
            target_entity_id: matchId,
            page_size: 50,
          },
        }),
      ];
      if (userPersonaId) {
        requests.push(
          authAxios.get("/bio-claims", {
            params: {
              status: "pending",
              target_entity: "user_persona",
              target_entity_id: userPersonaId,
              page_size: 200,
            },
          })
        );
      }
      const [matchRes, narrativeRes, userRes] = await Promise.all(requests);
      setMatchClaims(sortClaims(matchRes.data?.items || []));
      setNarrativeRows(narrativeRes.data?.items || []);
      setUserClaims(userRes ? sortClaims(userRes.data?.items || []) : []);
    } catch (err) {
      console.error("Failed to fetch bio claims:", err);
      setError(err?.response?.data?.detail || "Failed to load bio updates.");
      setMatchClaims([]);
      setNarrativeRows([]);
      setUserClaims([]);
    } finally {
      setLoading(false);
    }
  }, [matchId, userPersonaId]);

  useEffect(() => {
    fetchClaims();
  }, [fetchClaims, refreshKey, reloadTick]);

  // Bubble the total pending count up to the parent so the tab badge can
  // render. Caller passes `onCountChange`; we call it whenever the data
  // changes.
  useEffect(() => {
    if (typeof onCountChange === "function") {
      onCountChange(matchClaims.length + userClaims.length + narrativeRows.length);
    }
  }, [matchClaims.length, userClaims.length, narrativeRows.length, onCountChange]);

  // Auto-select the side with more pending updates ONCE on first load
  // of this panel mount. Subsequent claim-count changes (from
  // approvals, edits, etc.) never re-trigger this so the sub-tab stays
  // put where the user is working. Narrative synthesis rows always live
  // on the match side.
  useEffect(() => {
    if (didAutoPickRef.current) return;
    if (loading) return;
    if (error) return;
    // Wait until the first fetch settles (both sides are known, even if 0).
    didAutoPickRef.current = true;
    const matchSide = matchClaims.length + narrativeRows.length;
    if (matchSide > userClaims.length) {
      setActiveSubTab("match");
    } else {
      setActiveSubTab("user");
    }
  }, [loading, error, matchClaims.length, userClaims.length, narrativeRows.length]);

  // Follow-up auto-switch: if the active sub-tab has been emptied (user
  // cleared everything on that side) but the OTHER side still has
  // pending updates, jump to the other side so the user doesn't stare
  // at an empty pane. Only fires when active === 0 and other > 0 — this
  // is intentional, so we never yank the tab from under an active
  // review session, only when there's genuinely nothing left to see.
  useEffect(() => {
    if (loading || error) return;
    const userCount = userClaims.length;
    const matchCount = matchClaims.length + narrativeRows.length;
    if (activeSubTab === "user" && userCount === 0 && matchCount > 0) {
      setActiveSubTab("match");
    } else if (activeSubTab === "match" && matchCount === 0 && userCount > 0) {
      setActiveSubTab("user");
    }
  }, [loading, error, activeSubTab, matchClaims.length, userClaims.length, narrativeRows.length]);

  const handleAction = useCallback(
    async (action, claim, extras = {}) => {
      setBusyClaimId(claim.claim_id);
      try {
        if (action === "approve") {
          await authAxios.post(`/bio-claims/${claim.claim_id}/approve`, {
            lock: !!extras.lock,
          });
        } else if (action === "modify") {
          await authAxios.post(`/bio-claims/${claim.claim_id}/modify`, {
            modified_value: extras.modified_value,
            lock: !!extras.lock,
          });
        } else if (action === "reject") {
          await authAxios.post(`/bio-claims/${claim.claim_id}/reject`, {});
        }
        setReloadTick((t) => t + 1);
      } catch (err) {
        console.error(`bio-claim ${action} failed:`, err);
        setError(err?.response?.data?.detail || `Could not ${action} update.`);
      } finally {
        setBusyClaimId(null);
      }
    },
    []
  );

  const handleNarrativeAction = useCallback(
    async (action, row, extras = {}) => {
      setBusySynthesisId(row.synthesis_id);
      try {
        if (action === "approve") {
          await authAxios.post(
            `/bio-claims/narrative/${row.synthesis_id}/approve`, {},
          );
        } else if (action === "modify") {
          await authAxios.post(
            `/bio-claims/narrative/${row.synthesis_id}/modify`,
            { modified_value: extras.modified_value },
          );
        } else if (action === "reject") {
          await authAxios.post(
            `/bio-claims/narrative/${row.synthesis_id}/reject`, {},
          );
        }
        setReloadTick((t) => t + 1);
      } catch (err) {
        console.error(`narrative synthesis ${action} failed:`, err);
        setError(err?.response?.data?.detail || `Could not ${action} synthesis.`);
      } finally {
        setBusySynthesisId(null);
      }
    },
    []
  );

  // Eligible-for-batch claims: Tier 1 HIGH pending, across both columns.
  const batchEligible = useMemo(() => {
    const all = [...matchClaims, ...userClaims];
    return all.filter((c) => c.field_tier === 1 && c.confidence === "high");
  }, [matchClaims, userClaims]);

  const handleBatchApprove = async () => {
    if (batchEligible.length < BATCH_MIN_CLAIMS) return;
    setBatchBusy(true);
    try {
      await authAxios.post("/bio-claims/batch-approve", {
        claim_ids: batchEligible.map((c) => c.claim_id),
      });
      setReloadTick((t) => t + 1);
    } catch (err) {
      console.error("batch approve failed:", err);
      setError(err?.response?.data?.detail || "Batch approve failed.");
    } finally {
      setBatchBusy(false);
    }
  };

  if (loading) {
    return (
      <div
        className="flex items-center justify-center py-12 text-white/50"
        data-testid="bio-claims-loading"
      >
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading bio updates…
      </div>
    );
  }

  const total = matchClaims.length + userClaims.length + narrativeRows.length;

  if (total === 0) {
    return (
      <div
        className="rounded-md border border-divine-gold/15 bg-divine-black/30 p-6 text-center space-y-2"
        data-testid="bio-claims-empty"
      >
        <Sparkle className="w-5 h-5 mx-auto text-divine-gold/60" />
        <div className="text-sm text-white/70">
          Nothing waiting for review.
        </div>
        <div className="text-xs text-white/40">
          As coaching sessions surface new things about {safeMatchName} or {safeUserPersonaName},
          updates will appear here for you to approve.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="bio-claims-panel">
      {/* Header: refresh + batch */}
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm text-white/70">
          {total} {total === 1 ? "update" : "updates"} awaiting review
        </div>
        <div className="flex items-center gap-2">
          {batchEligible.length >= BATCH_MIN_CLAIMS && (
            <button
              type="button"
              disabled={batchBusy}
              onClick={handleBatchApprove}
              className="px-3 py-1 text-xs rounded bg-divine-gold/20 hover:bg-divine-gold/30 text-divine-gold font-medium border border-divine-gold/40 flex items-center gap-1 disabled:opacity-40"
              data-testid="bio-claims-batch-approve"
            >
              {batchBusy ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <CheckCircle2 className="w-3 h-3" />
              )}
              Approve {batchEligible.length} basic identity updates
            </button>
          )}
          <button
            type="button"
            onClick={() => setReloadTick((t) => t + 1)}
            className="p-1 text-white/50 hover:text-white"
            data-testid="bio-claims-refresh"
            title="Refresh"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {error && (
        <div
          className="flex items-start gap-2 rounded border border-rose-500/30 bg-rose-500/10 p-2 text-xs text-rose-200"
          data-testid="bio-claims-error"
        >
          <AlertCircle className="w-3.5 h-3.5 mt-[2px]" />
          <div>{error}</div>
        </div>
      )}

      <Tabs
        value={activeSubTab}
        onValueChange={(v) => {
          // Manual user pick — also latch the auto-pick guard so we
          // never fight the user if the first fetch is still in flight.
          didAutoPickRef.current = true;
          setActiveSubTab(v);
        }}
        className="w-full"
      >
        <TabsList
          className="bg-divine-black/40 border border-divine-gold/15"
          data-testid="bio-claims-subtabs"
        >
          <TabsTrigger
            value="user"
            className="data-[state=active]:bg-divine-gold/15 data-[state=active]:text-divine-gold text-white/70"
            data-testid="bio-claims-subtab-user"
          >
            <span>{userFirst}</span>
            {userClaims.length > 0 && (
              <span
                className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-divine-gold/20 text-divine-gold"
                data-testid="bio-claims-subtab-user-badge"
              >
                {userClaims.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger
            value="match"
            className="data-[state=active]:bg-divine-gold/15 data-[state=active]:text-divine-gold text-white/70"
            data-testid="bio-claims-subtab-match"
          >
            <span>{matchTabLabel}</span>
            {(matchClaims.length + narrativeRows.length) > 0 && (
              <span
                className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-divine-gold/20 text-divine-gold"
                data-testid="bio-claims-subtab-match-badge"
              >
                {matchClaims.length + narrativeRows.length}
              </span>
            )}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="user" className="mt-3">
          <PersonaPane
            personaName={safeUserPersonaName}
            claims={userClaims}
            onAction={handleAction}
            busyClaimId={busyClaimId}
            emptyHint={`Nothing pending about ${safeUserPersonaName} right now.`}
          />
        </TabsContent>
        <TabsContent value="match" className="mt-3 space-y-3">
          {narrativeRows.length > 0 && (
            <div
              className="space-y-2"
              data-testid="narrative-synthesis-list"
            >
              <div className="text-[11px] uppercase tracking-wider text-white/50">
                Relationship narrative ({narrativeRows.length})
              </div>
              {narrativeRows.map((row) => (
                <NarrativeSynthesisCard
                  key={row.synthesis_id}
                  row={row}
                  busy={busySynthesisId === row.synthesis_id}
                  onAction={handleNarrativeAction}
                  personaFirst={matchFirst}
                />
              ))}
            </div>
          )}
          <PersonaPane
            personaName={safeMatchName}
            claims={matchClaims}
            onAction={handleAction}
            busyClaimId={busyClaimId}
            emptyHint={
              narrativeRows.length > 0
                ? `No field-level updates pending for ${safeMatchName}.`
                : `Nothing pending about ${safeMatchName} right now.`
            }
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
